import type { ReactNode } from 'react'
import {
  type AgentRunDetail,
  type AgentSubmission,
  formatCost,
  formatCount,
  formatLatency,
  getRunTotalCost,
  MATCH_SIDE_LABELS,
  MATCH_VERDICT_LABELS,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { AGREEMENT_STYLES, MATCH_STATUS_NOTES } from '../config'
import {
  type Agreement,
  compareToProduction,
  describeProductionOutcome,
  isProductionAbstained,
  type RunReview,
} from '../lib'
import { AgreementBadge, MaterialIcon, Muted } from './primitives'

const HEADLINES: Record<Agreement, string> = {
  agree: 'The agent got this document right',
  partial: 'The agent got this document partly right',
  differ: "The agent's result differs from the final document",
  pending: 'Not reviewed yet, so the agent cannot be scored',
  none: 'Nothing to score in this run',
}

function joinWords(words: string[]): string {
  return words.length > 1
    ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
    : words.join('')
}

function namedCodeDiff(rows: RunReview['codes']['rows']): string | null {
  const missed = rows.filter((row) => row.agent === 'missed').map((row) => row.code)
  const extra = rows.filter((row) => row.agent === 'extra').map((row) => row.code)
  const parts = [
    missed.length ? `Missed ${missed.join(', ')}` : null,
    extra.length ? `Added ${extra.join(', ')}` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

function codesSummary(tally: { agreed: number; missed: number; extra: number }): string {
  const total = tally.agreed + tally.missed
  const parts = [
    total ? `${tally.agreed} of ${total} final ${pluralize(total, 'code')}` : 'No final codes',
    tally.missed ? `${tally.missed} missed` : null,
    tally.extra ? `${tally.extra} extra` : null,
  ]
  return parts.filter(Boolean).join(' · ')
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function CheckTile({
  title,
  agreement,
  agreementLabel,
  value,
  detail,
  today,
  todayLabel,
  todayDetail,
  target,
}: {
  title: string
  agreement: Agreement
  agreementLabel?: string
  value: ReactNode
  detail?: ReactNode
  today: Agreement
  todayLabel?: string
  todayDetail?: string | null
  target: string
}) {
  const style = AGREEMENT_STYLES[agreement]
  return (
    <button
      type="button"
      onClick={() => scrollTo(target)}
      className={cn(
        'group border-divider bg-surface-lowest flex min-w-0 cursor-pointer flex-col gap-2 rounded-xl border p-4 text-left transition-shadow hover:shadow-[0_2px_10px_rgba(0,0,0,0.08)]',
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="text-text-secondary text-[12px] leading-4 font-medium tracking-[0.4px] uppercase">
          {title}
        </span>
        <MaterialIcon
          name="arrow_downward"
          className="text-text-tertiary text-[16px] opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>
      <div
        className={cn('flex items-center gap-1.5 text-[17px] leading-6 font-semibold', style.text)}
      >
        <MaterialIcon
          name={style.icon}
          className="text-[20px]"
        />
        {agreementLabel ?? style.label}
      </div>
      <div className="flex min-h-10 flex-col gap-0.5">
        <span className="text-text-primary line-clamp-2 text-[13px] leading-5">{value}</span>
        {detail && <Muted>{detail}</Muted>}
      </div>
      <div className="border-divider mt-auto flex w-full flex-wrap items-center gap-x-2 gap-y-1 border-t pt-2">
        <Muted>Production</Muted>
        <AgreementBadge
          agreement={today}
          label={todayLabel}
          tip={todayDetail}
        />
      </div>
    </button>
  )
}

export function VerdictHero({ run, review }: { run: AgentRunDetail; review: RunReview }) {
  const { category, codes, match } = review
  const submission = (run.result?.submission ?? null) as AgentSubmission | null
  const issues = submission?.issues ?? []
  const pick = match?.side ? run.match?.[match.side]?.pick : null
  const verdict = match?.sideData?.verdict
  const todayOutcome = match?.sideData?.reviewer?.outcome
  const todayAbstained = isProductionAbstained(todayOutcome)
  const skipped = run.run_status.startsWith('skipped')
  const failed = !run.final && !skipped && run.run_status !== 'completed'

  const versus = compareToProduction([
    [category.agent.agreement, category.today.agreement],
    [codes.agent.agreement, codes.today.agreement],
    [match?.agent ?? 'none', match?.today ?? 'none'],
  ])
  const versusParts = [
    versus.better ? `better on ${versus.better}` : null,
    versus.same ? `the same on ${versus.same}` : null,
    versus.worse ? `worse on ${versus.worse}` : null,
  ].filter(Boolean)
  const checks = versus.better + versus.same + versus.worse

  const settled: Array<[string, Agreement]> = [
    ['category', category.agent.agreement],
    ['codes', codes.agent.agreement],
    ['patient case', match?.agent ?? 'none'],
  ]
  const agreesOn = settled.filter(([, value]) => value === 'agree').map(([name]) => name)
  const scored = settled.filter(
    ([, value]) => value === 'agree' || value === 'partial' || value === 'differ',
  ).length
  const differsOn = settled
    .filter(([, value]) => value === 'differ' || value === 'partial')
    .map(([name]) => name)

  const metrics = [
    formatCost(getRunTotalCost(run)),
    formatLatency((run.latency_ms ?? 0) + (run.match_latency_ms ?? 0) || null),
    `${formatCount((run.model_calls ?? 0) + (run.match_model_calls ?? 0))} model calls`,
  ]

  const matchValue = (() => {
    if (!match) return 'No patient matching in this run'
    if (!run.final) return 'The run stopped before matching'
    if (match.status !== 'completed')
      return MATCH_STATUS_NOTES[match.status] ?? `Matching ${match.status}`
    if (match.excluded) return `Not scored: the document is excluded (${match.excluded})`
    if (!match.side) return 'No side to score'
    if (!pick) return `${MATCH_SIDE_LABELS[match.side]}: no decision`
    const pickIds = match.sideData?.row_marks.flat().find((mark) => mark.agent)?.ids ?? []
    const choice =
      pick.decision === 'none'
        ? 'no matching case'
        : pickIds.length
          ? `case ${pickIds.join(' / ')}`
          : [pick.decision, pick.candidate].filter(Boolean).join(' ')
    const tie = pick.decision === 'tie' ? ` (a tie among ${pick.tied?.length ?? 0})` : ''
    return `${MATCH_SIDE_LABELS[match.side]} ${choice}${tie}${pick.confidence ? ` · ${pick.confidence} confidence` : ''}`
  })()

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 px-1">
        <h1
          className={cn(
            'm-0 flex items-center gap-2 text-[24px] leading-8 font-semibold',
            failed ? 'text-alert-error-content' : 'text-text-primary',
          )}
        >
          {failed
            ? 'The agent did not finish this run'
            : skipped
              ? 'The agent skipped this document'
              : HEADLINES[review.overall]}
        </h1>
        <p className="text-text-secondary m-0 text-[14px] leading-5">
          {failed || skipped
            ? (run.error ?? `Run status: ${run.run_status.replace(/[_:]/g, ' ')}.`)
            : [
                scored
                  ? `${agreesOn.length} of ${scored} ${pluralize(scored, 'check')} match the final document${agreesOn.length ? ` (${joinWords(agreesOn)})` : ''}.`
                  : null,
                differsOn.length ? `Different on the ${joinWords(differsOn)}.` : null,
                checks
                  ? `Compared with production, the agent did ${versusParts.join(', ')}.`
                  : null,
              ]
                .filter(Boolean)
                .join(' ') || 'The final document has not been settled by a review yet.'}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <CheckTile
          title="Category"
          agreement={category.agent.agreement}
          value={category.agent.label ?? 'No category'}
          detail={
            category.agent.agreement === 'differ' || category.agent.agreement === 'partial'
              ? `Final: ${category.final.label}`
              : null
          }
          today={category.today.agreement}
          todayDetail={category.today.label ? `Production said ${category.today.label}` : null}
          target="final-state"
        />
        <CheckTile
          title="Codes"
          agreement={codes.agent.agreement}
          value={
            !run.final
              ? 'No result to compare'
              : codes.hasFinal
                ? codesSummary(codes.agent.tally)
                : `${codes.agent.tally.agreed + codes.agent.tally.extra} codes; the tags were not reviewed`
          }
          detail={run.final && codes.hasFinal ? namedCodeDiff(codes.rows) : null}
          today={codes.today.agreement}
          todayDetail={codes.hasFinal ? codesSummary(codes.today.tally) : null}
          target="codes"
        />
        <CheckTile
          title="Patient case"
          agreement={match?.agent ?? 'none'}
          agreementLabel={
            verdict && verdict !== 'correct' && verdict !== 'wrong'
              ? MATCH_VERDICT_LABELS[verdict]?.label
              : undefined
          }
          value={matchValue}
          detail={verdict ? MATCH_VERDICT_LABELS[verdict]?.description : null}
          today={match?.today ?? 'none'}
          todayLabel={todayAbstained ? 'Abstained' : undefined}
          todayDetail={describeProductionOutcome(todayOutcome)}
          target="patient-case"
        />
      </div>

      {issues.length > 0 && (
        <div className="bg-swatch-yellow-50 text-swatch-yellow-900 flex items-start gap-2 rounded-xl px-4 py-3 text-[13px] leading-5">
          <MaterialIcon
            name="flag"
            className="mt-0.5 text-[18px]"
          />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">The agent flagged a problem with this document</span>
            {issues.map((issue, index) => (
              <span key={index}>
                {[
                  issue.kind.replace(/_/g, ' '),
                  issue.detail,
                  issue.pages?.length ? `pages ${issue.pages.join(', ')}` : null,
                ]
                  .filter(Boolean)
                  .join(' — ')}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <Muted>{metrics.join(' · ')}</Muted>
        <button
          type="button"
          onClick={() => scrollTo('run-details')}
          className="text-text-link cursor-pointer border-none bg-transparent p-0 text-[12px] leading-4 hover:underline"
        >
          Run details
        </button>
      </div>
    </section>
  )
}
